import type { TasApplicationState } from 'tio-common/types/tuition-assistance';
import type { ChipSignature } from 'tio-ui/components/chip';

// Valid Chip intents from tio-ui
export type ChipIntent = 'primary' | 'success' | 'warning' | 'danger' | 'default';

// Icon types for status indicator
export type StatusIconType = 'clock' | 'warning' | 'check' | 'x';

export interface StatusInfo {
  label: string;
  labelKey: string;
  intent: ChipIntent;
  icon: StatusIconType;
}

/**
 * Consolidated status display mapping for TAS Application states
 * Used by dashboard components for consistent status display
 */
export const APPLICATION_STATUS_MAP: Record<string, StatusInfo> = {
  'TAS.ApplicationState.CREATED': {
    label: 'Created',
    labelKey: 'common.tas.state_badge.created',
    intent: 'primary',
    icon: 'clock',
  },
  'TAS.ApplicationState.DEFINE_COURSES': {
    label: 'Draft',
    labelKey: 'common.tas.state_badge.draft',
    intent: 'primary',
    icon: 'clock',
  },
  'TAS.ApplicationState.ATTEND': {
    label: 'In Progress',
    labelKey: 'common.tas.state_badge.in_progress',
    intent: 'primary',
    icon: 'clock',
  },
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': {
    label: 'Pending Courses Approval',
    labelKey: 'common.tas.state_badge.pending_courses_approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.COURSES_APPROVED': {
    label: 'Approved',
    labelKey: 'common.tas.state_badge.approved',
    intent: 'success',
    icon: 'clock',
  },
  'TAS.ApplicationState.COURSES_NOT_APPROVED': {
    label: 'Not Approved',
    labelKey: 'common.tas.state_badge.not_approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.COURSES_REJECTED': {
    label: 'Rejected',
    labelKey: 'common.tas.state_badge.rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.PENDING_EVIDENCE_SUBMISSION': {
    label: 'Awaiting Evidence',
    labelKey: 'common.tas.state_badge.awaiting_evidence',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': {
    label: 'Pending Evidence Approval',
    labelKey: 'common.tas.state_badge.pending_evidence_approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.EVIDENCE_APPROVED': {
    label: 'Evidence Approved',
    labelKey: 'common.tas.state_badge.evidence_approved',
    intent: 'success',
    icon: 'clock',
  },
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': {
    label: 'Evidence Not Approved',
    labelKey: 'common.tas.state_badge.evidence_not_approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.EVIDENCE_REJECTED': {
    label: 'Evidence Rejected',
    labelKey: 'common.tas.state_badge.evidence_rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.REJECTED_PER_POLICY': {
    label: 'Rejected Per Policy',
    labelKey: 'common.tas.state_badge.rejected_per_policy',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ApplicationState.PENDING_PAYMENT': {
    label: 'Ready for Payment',
    labelKey: 'common.tas.state_badge.ready_for_payment',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ApplicationState.FULFILLED': {
    label: 'Complete',
    labelKey: 'common.tas.state_badge.complete',
    intent: 'success',
    icon: 'check',
  },
  'TAS.ApplicationState.ABANDONED': {
    label: 'Abandoned',
    labelKey: 'common.tas.state_badge.abandoned',
    intent: 'default',
    icon: 'warning',
  },
};

/**
 * Consolidated status display mapping for TAS Program Instance states
 * Used for Type 4 program instances
 */
export const PROGRAM_INSTANCE_STATUS_MAP: Record<string, StatusInfo> = {
  'TAS.ProgramInstanceState.CREATED': {
    label: 'Draft',
    labelKey: 'common.tas.state_badge.draft',
    intent: 'primary',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.DRAFT': {
    label: 'Draft',
    labelKey: 'common.tas.state_badge.draft',
    intent: 'primary',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL': {
    label: 'Pending Program Approval',
    labelKey: 'common.tas.state_badge.pending_program_approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.PROGRAM_APPROVED': {
    label: 'Approved',
    labelKey: 'common.tas.state_badge.approved',
    intent: 'success',
    icon: 'check',
  },
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED': {
    label: 'Not Approved',
    labelKey: 'common.tas.state_badge.not_approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.PROGRAM_REJECTED': {
    label: 'Rejected',
    labelKey: 'common.tas.state_badge.rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.PRIMED': {
    label: 'In Progress',
    labelKey: 'common.tas.state_badge.in_progress',
    intent: 'primary',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL': {
    label: 'Pending Completion Approval',
    labelKey: 'common.tas.state_badge.pending_completion_approval',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.COMPLETION_APPROVED': {
    label: 'Completed',
    labelKey: 'common.tas.state_badge.completed',
    intent: 'success',
    icon: 'check',
  },
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED': {
    label: 'Not Approved',
    labelKey: 'common.tas.state_badge.not_approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.COMPLETION_REJECTED': {
    label: 'Rejected',
    labelKey: 'common.tas.state_badge.rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL': {
    label: 'Withdrawal Pending',
    labelKey: 'common.tas.state_badge.withdrawal_pending',
    intent: 'warning',
    icon: 'clock',
  },
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED': {
    label: 'Withdrawn',
    labelKey: 'common.tas.state_badge.withdrawn',
    intent: 'default',
    icon: 'check',
  },
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED': {
    label: 'Withdrawal Not Approved',
    labelKey: 'common.tas.state_badge.withdrawal_not_approved',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': {
    label: 'Withdrawal Rejected',
    labelKey: 'common.tas.state_badge.withdrawal_rejected',
    intent: 'default',
    icon: 'x',
  },
  'TAS.ProgramInstanceState.COMPLETED': {
    label: 'Completed',
    labelKey: 'common.tas.state_badge.completed',
    intent: 'success',
    icon: 'check',
  },
  'TAS.ProgramInstanceState.WITHDRAWN': {
    label: 'Withdrawn',
    labelKey: 'common.tas.state_badge.withdrawn',
    intent: 'default',
    icon: 'warning',
  },
  'TAS.ProgramInstanceState.ABANDONED': {
    label: 'Abandoned',
    labelKey: 'common.tas.state_badge.abandoned',
    intent: 'default',
    icon: 'warning',
  },
};

const tasApplicationStateCourseBadgeMap: Record<
  string,
  { intent: ChipSignature['Args']['intent']; label: string; labelKey: string }
> = {
  'TAS.ApplicationState.CREATED': {
    intent: 'warning',
    label: 'Add Courses',
    labelKey: 'common.tas.state_badge.add_courses',
  },
  'TAS.ApplicationState.DEFINE_COURSES': {
    intent: 'warning',
    label: 'Add Courses',
    labelKey: 'common.tas.state_badge.add_courses',
  },
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': {
    intent: 'primary',
    label: 'Pending',
    labelKey: 'common.tas.state_badge.pending',
  },
  'TAS.ApplicationState.COURSES_APPROVED': {
    intent: 'success',
    label: 'Approved',
    labelKey: 'common.tas.state_badge.approved',
  },
  'TAS.ApplicationState.COURSES_NOT_APPROVED': {
    intent: 'danger',
    label: 'Rejected',
    labelKey: 'common.tas.state_badge.rejected',
  },
  'TAS.ApplicationState.COURSES_REJECTED': {
    intent: 'danger',
    label: 'Rejected',
    labelKey: 'common.tas.state_badge.rejected',
  },
  'TAS.ApplicationState.ATTEND': {
    intent: 'success',
    label: 'In Progress',
    labelKey: 'common.tas.state_badge.in_progress',
  },
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': {
    intent: 'primary',
    label: 'Pending',
    labelKey: 'common.tas.state_badge.pending',
  },
  'TAS.ApplicationState.EVIDENCE_APPROVED': {
    intent: 'success',
    label: 'In Progress',
    labelKey: 'common.tas.state_badge.in_progress',
  },
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': {
    intent: 'danger',
    label: 'Rejected',
    labelKey: 'common.tas.state_badge.rejected',
  },
  'TAS.ApplicationState.EVIDENCE_REJECTED': {
    intent: 'danger',
    label: 'Rejected',
    labelKey: 'common.tas.state_badge.rejected',
  },
  'TAS.ApplicationState.FULFILLED': {
    intent: 'success',
    label: 'Completed',
    labelKey: 'common.tas.state_badge.completed',
  },
  'TAS.ApplicationState.ABANDONED': {
    intent: 'warning',
    label: 'Abandoned',
    labelKey: 'common.tas.state_badge.abandoned',
  },
};

export const courseBadgeState = (state: TasApplicationState) => {
  return tasApplicationStateCourseBadgeMap[state];
};
